import { Drawer } from '@dynamika-shared/ui/Drawer/Drawer';
import { DrawerProps } from 'antd';
import { FC, useCallback, useMemo, useState } from 'react';
import { generateOptions } from '@utils/generateOptions';
import { Button } from '@dynamika-shared/ui/Button/Button';
import { Input } from '@dynamika-shared/ui/Input/Input';
import { useForm } from 'react-hook-form';
import { Select } from '@dynamika-shared/ui/Select/Select';
import { SearchIcon } from '@dynamika-shared/ui/icons';
import { NoDataSelect } from '@dynamika-shared/ui/NoDataSelect/NoDataSelect';
import {
  CustomOptionType,
  OptionRenderer,
} from '@components/OptionRenderer/OptionRenderer';
import { UnsavedChangesModal } from '@components/modals/UnsavedChangesModal/UnsavedChangesModal';
import { useQuestionnairesSlice } from '@restore/questionnaires/questionnaires.slice';
import { Questionnaire } from '@dynamika-shared/types/questionnaire';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { drawersProps } from 'src/pages/ClinicalTrial/ClinicalTrialForms/drawers/index';
import { useParams } from 'react-router-dom';
import { TemplateFormType } from '@restore/questionnaires/questionnaires.types';
import { debounce } from 'lodash';

type AddTemplateDrawerProps = DrawerProps & {
  close?: () => void;
  currentTemplate?: Partial<Questionnaire>;
};

type FieldName = keyof TemplateFormType;

export const AddTemplateDrawer: FC<AddTemplateDrawerProps> = ({ close }) => {
  const { id } = useParams();
  const [questionnaires, fetchQuestionnaires, addClinicalTrialQuestionnaire] =
    useQuestionnairesSlice((state, actions) => [
      state.questionnaires,
      actions.fetchQuestionnaires,
      actions.addClinicalTrialQuestionnaireAction,
    ]);

  const [searchValue, setSearchValue] = useState<string>('');
  const [askQuit, setAskQuit] = useState<boolean>(false);

  const { watch, setValue, formState, trigger, getValues } =
    useForm<TemplateFormType>({
      resolver: yupResolver(
        yup.object().shape({
          questionnaireId: yup.string().required(),
          alias: yup.string().required().min(3).max(255),
          name: yup.string().required().min(3).max(255),
        })
      ),
    });

  const { errors, isValid, isDirty, touchedFields } = formState;

  const disabled = !isValid;

  const questionnairesOptions = useMemo(() => {
    return generateOptions(
      questionnaires.map(questionnaire => ({
        id: questionnaire.id,
        name: questionnaire.name,
      })),
      'id',
      'name'
    );
  }, [questionnaires]);

  const renderOption = useCallback(
    (option: CustomOptionType) => {
      const selected = questionnairesOptions.find(
        el => el.value === watch('questionnaireId')
      );
      return (
        <OptionRenderer
          searchValue={searchValue}
          option={option}
          selectedOptions={selected ? [selected] : []}
          select={'Select'}
          selected={'Selected'}
        />
      );
    },
    [searchValue, questionnairesOptions]
  );

  const delayedSearch = useMemo(
    () =>
      debounce((name?: string) => {
        return fetchQuestionnaires({ name });
      }, 500),
    []
  );

  const onCloseDrawer = useCallback(() => {
    const shouldAskQuit = isDirty || Object.keys(touchedFields).length > 0;
    shouldAskQuit ? setAskQuit(true) : close?.();
  }, [touchedFields, close]);

  const onBlur = (fieldName: FieldName, value: string) => {
    if (touchedFields[fieldName]) {
      onChange(fieldName, value.trim());
    }
  };
  const onChange = (name: FieldName, value: string) => {
    setValue(name, value, { shouldDirty: true, shouldTouch: true });
    trigger(name);
  };
  const handleAddTemplate = () => {
    addClinicalTrialQuestionnaire({ id, data: getValues() });
    close && close();
  };

  return (
    <Drawer
      {...drawersProps}
      data-testid='template-form-drawer'
      title={'Add Template'}
      placement='right'
      onClose={onCloseDrawer}
    >
      <div className='px-1'>
        <Select
          className='mb-2'
          label={'Select Template'}
          menuItemSelectedIcon={null}
          suffixIcon={<SearchIcon className='h-2 w-2' />}
          required
          options={questionnairesOptions}
          placeholder={'Search Template'}
          filterOption={false}
          notFoundContent={
            searchValue && !questionnairesOptions.length ? (
              <NoDataSelect
                text={searchValue}
                maxLength={10}
                name={'template'}
              />
            ) : null
          }
          searchValue={searchValue}
          onSearch={value => {
            setSearchValue(value);
            delayedSearch(value);
          }}
          value={watch('questionnaireId')}
          showSearch
          listHeight={160}
          onSelect={value => onChange('questionnaireId', value)}
          optionRender={renderOption}
          name='questionnaires'
          data-testid='template-questionnaires'
        />
        <Input
          required
          value={watch('name')}
          name={'name'}
          onChange={e => onChange('name', e.target.value)}
          onBlur={e => onBlur('name', e.target.value)}
          data-testid={'form-name'}
          errors={errors.name?.message as string}
          label={'Add form name'}
          placeholder={'Add form name'}
          classNameInputWrapper='mb-3'
        />
        <Input
          required
          value={watch('alias')}
          name={'alias'}
          onChange={e => onChange('alias', e.target.value)}
          onBlur={e => onBlur('alias', e.target.value)}
          data-testid={'template-alias'}
          errors={errors.alias?.message as string}
          label={'Add Alias'}
          placeholder={'Add Alias'}
          classNameInputWrapper='mb-3'
        />
      </div>
      <footer className='mt-auto pr-2'>
        <hr className='mb-3' />
        <div className='flex justify-between'>
          <Button className='w-[17rem]' onClick={onCloseDrawer}>
            Cancel
          </Button>
          <Button
            data-testid='template-save-btn'
            className='w-[17rem]'
            type='primary'
            disabled={disabled}
            onClick={handleAddTemplate}
          >
            Add
          </Button>
        </div>
      </footer>
      <UnsavedChangesModal
        onConfirm={() => close && close()}
        onCancel={() => setAskQuit(false)}
        open={askQuit}
      />
    </Drawer>
  );
};
